import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  RefreshCw,
  Loader2,
  AlertCircle,
  Lightbulb,
  Eye,
  EyeOff,
  Trash2,
  TrendingUp,
  Brain,
  Sparkles,
  Settings,
  CheckCircle2,
  ChevronRight,
  Shield
} from 'lucide-react';
import { Button, Card, EmptyState } from '../../components/ui';
import { AppLayout } from '../../components/AppLayout';
import { useAuth } from '../../context';
import {
  pulseService,
  UserPatternDto,
  UserInsightDto,
  UserPulseSettingsDto
} from '../../services';
import './Pulse.css';

const PROACTIVITY_LABELS: Record<number, { label: string; desc: string }> = {
  1: { label: 'Quiet', desc: 'Only show insights when you ask' },
  2: { label: 'Gentle', desc: 'Subtle nudges in your dashboard' },
  3: { label: 'Active', desc: 'Regular insights and suggestions' },
  4: { label: 'Engaged', desc: 'Proactive coaching and detailed analysis' },
};

const getPatternIcon = (type: string) => {
  if (type.includes('time') || type.includes('window')) return <Activity size={18} />;
  if (type.includes('mood') || type.includes('emotion')) return <Brain size={18} />;
  if (type.includes('streak') || type.includes('consistency')) return <TrendingUp size={18} />;
  return <Sparkles size={18} />;
};

const getConfidenceLabel = (confidence: number): { label: string; className: string } => {
  if (confidence >= 0.8) return { label: 'Strong pattern', className: 'pulse__confidence--high' };
  if (confidence >= 0.5) return { label: 'Emerging', className: 'pulse__confidence--medium' };
  return { label: 'Early signal', className: 'pulse__confidence--low' };
};

const describePattern = (type: string, json: string): string => {
  try {
    const data = JSON.parse(json);

    switch (type) {
      case 'best_creative_windows':
        return `Your most productive time is ${data.bestDay}s around ${data.bestHour}:00, based on ${data.totalSessions} sessions.`;
      case 'stage_abandonment':
        return `Works are most often set aside at the "${data.mostAbandonedStage}" stage (${data.totalAbandoned} total).`;
      case 'mood_output_correlation':
        return `Your most common mood is "${data.mostCommonMood}" across ${data.totalCheckIns} check-ins.`;
      case 'burst_fade_cycle':
        return data.hasBurstFadePattern
          ? `Burst-and-fade cycle detected: ${data.highActivityWeeks} high weeks, ${data.lowActivityWeeks} quiet weeks.`
          : `Consistent activity pattern — average ${(data.averageWeeklyActivity as number)?.toFixed(1) ?? '?'} actions/week.`;
      case 'avoidance_escape':
        return `New work started ${data.newWorkEscapeCount} times shortly after stage changes (${(data.escapeRate * 100).toFixed(1)}% escape rate).`;
      case 'intention_follow_through':
        return `Followed through on ${(data.followThroughRate * 100).toFixed(1)}% of intentions (${data.intentionsFollowed}/${data.intentionsSet}).`;
      case 'morning_pages_sentiment':
        return `Sentiment trend: ${data.sentimentTrend} across ${data.totalEntries} journal entries.`;
      case 'self_talk_patterns':
        return data.negativePatternCount >= 3
          ? `${data.negativePatternCount} instances of self-critical language detected across ${data.totalEntries} entries.`
          : `Generally positive self-talk across ${data.totalEntries} entries.`;
      case 'pre_abandonment_sentiment':
        return data.correlationDetected
          ? 'Sentiment drops detected before works are set aside.'
          : 'Not enough data yet to identify pre-abandonment sentiment patterns.';
      case 'block_kit_effectiveness': {
        const rate = ((data.effectivenessRate ?? 0) * 100).toFixed(1);
        return `Block kits used ${data.totalBlockKitUsages} times. Work progressed within 3 days in ${data.effectiveUsages ?? 0} cases (${rate}%).`;
      }
      case 'idea_velocity':
        return `Idea velocity: ${data.velocityTrend} — averaging ${(data.averageIdeasPerWeek as number)?.toFixed(1) ?? '?'} ideas/week (${data.totalIdeas} total).`;
      default:
        // Fallback: show top-level keys with safe value formatting
        return Object.entries(data)
          .slice(0, 3)
          .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
          .join(', ');
    }
  } catch {
    return json.slice(0, 100);
  }
};

type Tab = 'insights' | 'patterns' | 'settings' | 'privacy';

export const Pulse: React.FC = () => {
  useAuth();

  const [activeTab, setActiveTab] = useState<Tab>('insights');
  const [patterns, setPatterns] = useState<UserPatternDto[]>([]);
  const [insights, setInsights] = useState<UserInsightDto[]>([]);
  const [settings, setSettings] = useState<UserPulseSettingsDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [patternsData, insightsData, settingsData] = await Promise.all([
        pulseService.getPatterns().catch(() => []),
        pulseService.getInsights({ delivered: false }).catch(() => []),
        pulseService.getSettings().catch(() => null),
      ]);

      setPatterns(patternsData);
      setInsights(insightsData);
      setSettings(settingsData);
    } catch (err) {
      console.error('Failed to fetch pulse data:', err);
      setError('Failed to load Creative Pulse data.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      const result = await pulseService.analyzePatterns();
      setPatterns(result);
    } catch (err) {
      console.error('Pattern analysis failed:', err);
      setError('Pattern analysis failed. Try again later.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGenerateInsights = async () => {
    try {
      setIsGenerating(true);
      const result = await pulseService.generateInsights();
      setInsights(prev => [...result, ...prev]);
    } catch (err) {
      console.error('Insight generation failed:', err);
      setError('Insight generation failed. Try again later.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleMarkDelivered = async (id: string) => {
    try {
      await pulseService.markInsightDelivered(id);
      setInsights(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error('Failed to dismiss insight:', err);
    }
  };

  const handleUpdateSettings = async (update: Partial<UserPulseSettingsDto>) => {
    if (!settings) return;
    try {
      const result = await pulseService.updateSettings({
        proactivityLevel: update.proactivityLevel ?? settings.proactivityLevel,
        morningPagesInsightsEnabled: update.morningPagesInsightsEnabled ?? settings.morningPagesInsightsEnabled,
        weeklyDigestEnabled: update.weeklyDigestEnabled ?? settings.weeklyDigestEnabled,
      });
      setSettings(result);
    } catch (err) {
      console.error('Failed to update settings:', err);
      setError('Failed to save settings.');
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('This will permanently delete all your signal history. This action cannot be undone. Continue?')) return;
    try {
      await pulseService.clearSignalHistory();
      setPatterns([]);
      setInsights([]);
    } catch (err) {
      console.error('Failed to clear history:', err);
      setError('Failed to clear signal history.');
    }
  };

  if (isLoading) {
    return (
      <AppLayout pageTitle="Creative Pulse">
        <div className="pulse">
          <div className="pulse__loading">
            <Loader2 size={32} className="pulse__loading-spinner" />
            <p>Loading your creative pulse...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="Creative Pulse">
      <div className="pulse">
        {/* Ambient background */}
        <div className="pulse__ambient">
          <div className="pulse__ambient-shape pulse__ambient-shape--1" />
          <div className="pulse__ambient-shape pulse__ambient-shape--2" />
        </div>

        <main className="pulse__main">
          {/* Header */}
          <div className="pulse__header">
            <div className="pulse__header-info">
              <p className="pulse__subtitle">Your creative intelligence engine</p>
            </div>
            <Button variant="ghost" onClick={fetchData}>
              <RefreshCw size={16} />
            </Button>
          </div>

          {error && (
            <div className="pulse__inline-error">
              <AlertCircle size={14} />
              <span>{error}</span>
              <button className="pulse__dismiss" onClick={() => setError(null)}>&times;</button>
            </div>
          )}

          {/* Tabs */}
          <div className="pulse__tabs">
            <button
              className={`pulse__tab ${activeTab === 'insights' ? 'pulse__tab--active' : ''}`}
              onClick={() => setActiveTab('insights')}
            >
              <Lightbulb size={16} />
              <span>Insights</span>
              {insights.length > 0 && <span className="pulse__tab-badge">{insights.length}</span>}
            </button>
            <button
              className={`pulse__tab ${activeTab === 'patterns' ? 'pulse__tab--active' : ''}`}
              onClick={() => setActiveTab('patterns')}
            >
              <TrendingUp size={16} />
              <span>Patterns</span>
            </button>
            <button
              className={`pulse__tab ${activeTab === 'settings' ? 'pulse__tab--active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <Settings size={16} />
              <span>Settings</span>
            </button>
            <button
              className={`pulse__tab ${activeTab === 'privacy' ? 'pulse__tab--active' : ''}`}
              onClick={() => setActiveTab('privacy')}
            >
              <Shield size={16} />
              <span>Privacy</span>
            </button>
          </div>

          {/* Insights Tab */}
          {activeTab === 'insights' && (
            <section className="pulse__section">
              <div className="pulse__section-header">
                <h2 className="pulse__section-title">
                  <Lightbulb size={20} />
                  Active Insights
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateInsights}
                  disabled={isGenerating}
                >
                  <Sparkles size={14} className={isGenerating ? 'pulse__spin' : ''} />
                  {isGenerating ? 'Generating...' : 'Generate'}
                </Button>
              </div>

              {insights.length === 0 ? (
                <EmptyState
                  icon={<Lightbulb size={48} />}
                  title="No insights yet"
                  message="Use the app more to generate patterns, then generate insights from those patterns."
                  action={{ label: 'Generate Insights', onClick: handleGenerateInsights }}
                />
              ) : (
                <div className="pulse__insights-list">
                  {insights.map(insight => (
                    <Card key={insight.id} variant="default" padding="md" className="pulse__insight-card">
                      <div className="pulse__insight-header">
                        <span className="pulse__insight-type">{insight.insightType.replace(/_/g, ' ')}</span>
                        <span className={`pulse__insight-channel pulse__insight-channel--${insight.deliveryChannel.toLowerCase()}`}>
                          {insight.deliveryChannel}
                        </span>
                      </div>
                      <p className="pulse__insight-text">{insight.insightText}</p>
                      {insight.actionSuggestion && (
                        <div className="pulse__insight-action">
                          <ChevronRight size={14} />
                          <span>{insight.actionSuggestion}</span>
                        </div>
                      )}
                      <div className="pulse__insight-footer">
                        <span className="pulse__insight-date">
                          {new Date(insight.createdAt).toLocaleDateString()}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => handleMarkDelivered(insight.id)}>
                          <CheckCircle2 size={14} />
                          Dismiss
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Patterns Tab */}
          {activeTab === 'patterns' && (
            <section className="pulse__section">
              <div className="pulse__section-header">
                <h2 className="pulse__section-title">
                  <TrendingUp size={20} />
                  Detected Patterns
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAnalyze}
                  disabled={isAnalyzing}
                >
                  <Brain size={14} className={isAnalyzing ? 'pulse__spin' : ''} />
                  {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                </Button>
              </div>

              {patterns.length === 0 ? (
                <EmptyState
                  icon={<Brain size={48} />}
                  title="No patterns detected"
                  message="Keep using the app and your creative patterns will emerge over time."
                  action={{ label: 'Run Analysis', onClick: handleAnalyze }}
                />
              ) : (
                <div className="pulse__patterns-list">
                  {patterns.map(pattern => (
                    <Card key={pattern.id} variant="default" padding="md" className="pulse__pattern-card">
                      <div className="pulse__pattern-header">
                        <div className="pulse__pattern-icon">
                          {getPatternIcon(pattern.patternType)}
                        </div>
                        <div className="pulse__pattern-info">
                          <span className="pulse__pattern-type">{pattern.patternType.replace(/_/g, ' ')}</span>
                          <span className="pulse__pattern-points">{pattern.dataPointCount} data points</span>
                        </div>
                        <div className={`pulse__confidence ${getConfidenceLabel(pattern.confidence).className}`}>
                          {getConfidenceLabel(pattern.confidence).label}
                        </div>
                      </div>
                      <p className="pulse__pattern-data">{describePattern(pattern.patternType, pattern.patternDataJson)}</p>
                      <div className="pulse__pattern-footer">
                        <span>First detected: {new Date(pattern.firstDetectedAt).toLocaleDateString()}</span>
                        <span>Last confirmed: {new Date(pattern.lastConfirmedAt).toLocaleDateString()}</span>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && settings && (
            <section className="pulse__section">
              <h2 className="pulse__section-title">
                <Settings size={20} />
                Pulse Settings
              </h2>

              <Card variant="default" padding="md" className="pulse__settings-card">
                <h3 className="pulse__settings-label">Proactivity Level</h3>
                <p className="pulse__settings-desc">How actively should Creative Pulse reach out to you?</p>
                <div className="pulse__proactivity-options">
                  {[1, 2, 3, 4].map(level => (
                    <button
                      key={level}
                      className={`pulse__proactivity-btn ${settings.proactivityLevel === level ? 'pulse__proactivity-btn--active' : ''}`}
                      onClick={() => handleUpdateSettings({ proactivityLevel: level })}
                    >
                      <span className="pulse__proactivity-level">{level}</span>
                      <span className="pulse__proactivity-name">{PROACTIVITY_LABELS[level].label}</span>
                      <span className="pulse__proactivity-desc">{PROACTIVITY_LABELS[level].desc}</span>
                    </button>
                  ))}
                </div>
              </Card>

              <Card variant="default" padding="md" className="pulse__settings-card">
                <div className="pulse__toggle-row">
                  <div className="pulse__toggle-info">
                    <h3 className="pulse__settings-label">Morning Pages Insights</h3>
                    <p className="pulse__settings-desc">Analyze your morning pages for emotional patterns and creative themes.</p>
                  </div>
                  <button
                    className={`pulse__toggle ${settings.morningPagesInsightsEnabled ? 'pulse__toggle--on' : ''}`}
                    onClick={() => handleUpdateSettings({ morningPagesInsightsEnabled: !settings.morningPagesInsightsEnabled })}
                    aria-label="Toggle morning pages insights"
                  >
                    {settings.morningPagesInsightsEnabled ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                </div>
              </Card>

              <Card variant="default" padding="md" className="pulse__settings-card">
                <div className="pulse__toggle-row">
                  <div className="pulse__toggle-info">
                    <h3 className="pulse__settings-label">Weekly Digest</h3>
                    <p className="pulse__settings-desc">Receive a weekly summary of your creative patterns and insights.</p>
                  </div>
                  <button
                    className={`pulse__toggle ${settings.weeklyDigestEnabled ? 'pulse__toggle--on' : ''}`}
                    onClick={() => handleUpdateSettings({ weeklyDigestEnabled: !settings.weeklyDigestEnabled })}
                    aria-label="Toggle weekly digest"
                  >
                    {settings.weeklyDigestEnabled ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                </div>
              </Card>
            </section>
          )}

          {/* Privacy Tab */}
          {activeTab === 'privacy' && (
            <section className="pulse__section">
              <h2 className="pulse__section-title">
                <Shield size={20} />
                Privacy Controls
              </h2>

              <Card variant="default" padding="md" className="pulse__privacy-card">
                <h3 className="pulse__settings-label">Your Data</h3>
                <p className="pulse__settings-desc">
                  Creative Pulse collects signals from your app usage to detect patterns and generate insights.
                  All data is stored securely and never shared. You can delete your signal history at any time.
                </p>
                <div className="pulse__privacy-stats">
                  <div className="pulse__privacy-stat">
                    <span className="pulse__privacy-stat-value">{patterns.length}</span>
                    <span className="pulse__privacy-stat-label">Patterns detected</span>
                  </div>
                  <div className="pulse__privacy-stat">
                    <span className="pulse__privacy-stat-value">{insights.length}</span>
                    <span className="pulse__privacy-stat-label">Active insights</span>
                  </div>
                </div>
              </Card>

              <Card variant="default" padding="md" className="pulse__privacy-card pulse__privacy-card--danger">
                <h3 className="pulse__settings-label">Delete Signal History</h3>
                <p className="pulse__settings-desc">
                  Permanently delete all collected signals. This will reset your patterns and insights.
                  This action cannot be undone.
                </p>
                <Button variant="secondary" onClick={handleClearHistory} className="pulse__danger-btn">
                  <Trash2 size={16} />
                  Delete All Signal Data
                </Button>
              </Card>
            </section>
          )}
        </main>
      </div>
    </AppLayout>
  );
};

export default Pulse;
